import type { PetChatSummary } from "@/shared/contracts";
import { toApiError } from "@/shared/api/errors";
import { env } from "@/shared/config/env";
import { Button, Chip, EmptyState, LoadingState } from "@/shared/ui";
import { PetPortrait } from "@/features/pets/PetPortrait";
import { chatEndingLabel, chatStateOf } from "../petFriends";
import { chatTimeText } from "./usePetChats";
import "./petChats.css";

/**
 * 「TA 的朋友们」：TA 和朋友的聊天，一行一场（P2，6796 起草；只读，主人旁观；路由与接线归 6c2b）。
 * 一行：对方头像、名字、这一场聊得怎样（聊过／打过招呼／没接上／等回应／联系中，与朋友详情同一套说法）、句数、时间。
 * 契约的列表没有「最后一句」，所以这里不写原话；点进去看回放。
 * 空、读失败（可重试）、能力未接入（演示里如实说没有）各一句温和的话，不露码。看得见的字与读屏不写 AI、模型、生成。
 */

/** 不能列出时的那一句（与回放共用）。能力未接入：演示如实说没有，live 说现在还看不到；其余都当读失败，可重试。 */
export function petChatTrouble(error: unknown, name: string): { title: string; retry: boolean } {
  const err = toApiError(error);
  if (err.isCapabilityUnavailable) {
    return env.dataMode === "fixture" ? { title: `演示里没有 ${name} 和朋友的聊天。`, retry: false } : { title: `${name} 和朋友的聊天现在还看不到。`, retry: false };
  }
  if (err.isNotFound) return { title: "这场聊天已经看不到了。", retry: false };
  return { title: `没读到 ${name} 和朋友的聊天，等一下再试。`, retry: true };
}

function ChatRow({ item, onOpen, now }: { item: PetChatSummary; onOpen: (threadId: string) => void; now: Date }) {
  const state = chatStateOf(item);
  const ending = chatEndingLabel(item);
  const when = chatTimeText(item.ended_at ?? item.started_at, now);
  const count = item.line_count > 0 ? `${item.line_count} 句` : null;
  const detail = [ending, count].filter(Boolean).join(" · ");
  const spoken = [item.counterpart.name, state?.label, detail || null, when].filter(Boolean).join("，");
  return (
    <li>
      <button type="button" className="ps-pc-row" onClick={() => onOpen(item.thread_id)} aria-label={`${spoken}，看看聊了什么`}>
        <span className="ps-pc-row__avatar" aria-hidden="true">
          <PetPortrait name={item.counterpart.name} species={item.counterpart.species} size={44} />
        </span>
        <span className="ps-pc-row__main" aria-hidden="true">
          <span className="ps-pc-row__top">
            <strong className="ps-pc-row__name">{item.counterpart.name}</strong>
            {when ? <span className="ps-pc-row__time">{when}</span> : null}
          </span>
          {state || detail ? (
            <span className="ps-pc-row__sub">
              {state ? <Chip tone={state.tone}>{state.label}</Chip> : null}
              {detail ? <span className="ps-pc-row__detail">{detail}</span> : null}
            </span>
          ) : null}
        </span>
      </button>
    </li>
  );
}

export function PetChatList({ petName, items, loading, error, hasMore, loadingMore, onOpen, onMore, onRetry, now = new Date() }: {
  petName: string | null;
  items: readonly PetChatSummary[];
  loading: boolean;
  error: unknown;
  hasMore: boolean;
  loadingMore: boolean;
  onOpen: (threadId: string) => void;
  onMore: () => void;
  onRetry: () => void;
  now?: Date;
}) {
  const name = petName || "TA";
  if (loading) return <LoadingState label={`正在看 ${name} 和朋友聊了什么…`} lines={3} />;
  if (error && !items.length) {
    const trouble = petChatTrouble(error, name);
    return <EmptyState icon="comment" title={trouble.title} action={trouble.retry ? <Button variant="secondary" size="sm" onClick={onRetry}>再试一次</Button> : undefined} />;
  }
  if (!items.length) return <EmptyState icon="comment" title={`${name} 还没和朋友聊过天。`}>在外面遇到朋友、聊上了，就会出现在这里。</EmptyState>;
  return (
    <div className="ps-pc-list">
      <ul className="ps-pc-list__items" aria-label={`${name} 和朋友的聊天`}>
        {items.map((item) => <ChatRow key={item.thread_id} item={item} onOpen={onOpen} now={now} />)}
      </ul>
      {hasMore ? (
        <Button variant="secondary" size="sm" className="ps-pc-list__more" onClick={onMore} disabled={loadingMore}>
          {loadingMore ? "正在读更早的…" : "看更早的聊天"}
        </Button>
      ) : null}
    </div>
  );
}
